import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'Split Soul Active';
const SITE_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Organisation schema (homepage)
 */
export const OrganisationSchema = () => (
  <Helmet>
    <script type="application/ld+json">{JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Split Soul Active",
      "url": SITE_URL,
      "description": "Independent UK retailer of premium dance, activewear, yoga & pilates clothing and accessories.",
      "logo": `${SITE_URL}/logo-splitsoul.png`,
      "sameAs": [
        "https://www.instagram.com/splitsoulactive/",
        "https://www.facebook.com/splitsoulactive"
      ],
      "contactPoint": {
        "@type": "ContactPoint",
        "email": "shout@splitsoul.co.uk",
        "contactType": "customer service"
      }
    })}</script>
  </Helmet>
);

/**
 * Product schema (product detail page)
 */
export const ProductSchema = ({ product }) => {
  if (!product) return null;
  const schema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.name,
    "description": product.description?.replace(/<[^>]*>/g, '') || '',
    "brand": product.brand ? { "@type": "Brand", "name": product.brand } : undefined,
    "category": product.category || undefined,
    "image": product.images?.length > 0 ? product.images[0] : undefined,
    "offers": {
      "@type": "Offer",
      "priceCurrency": "GBP",
      "price": product.price?.toFixed(2),
      "availability": (product.stock > 0) ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      "seller": { "@type": "Organization", "name": "Split Soul Active" },
    },
  };
  if (product.average_rating > 0 && product.review_count > 0) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      "ratingValue": product.average_rating.toFixed(1),
      "reviewCount": product.review_count,
    };
  }
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

/**
 * Page-level meta tags (title, description, canonical, OG)
 */
export const PageMeta = ({ title, description, path, image }) => {
  const fullTitle = title ? `${title} — ${SITE_NAME}` : `${SITE_NAME} — Dancewear & Activewear`;
  const canonical = path ? `${SITE_URL}${path}` : SITE_URL;
  const desc = description || 'Independent UK retailer of premium dance, activewear, yoga & pilates gear. Curated brands, loyalty rewards, and event shopping.';

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={canonical} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      {image && <meta property="og:image" content={image} />}
    </Helmet>
  );
};

/**
 * Breadcrumb schema
 */
export const BreadcrumbSchema = ({ items }) => {
  if (!items || items.length === 0) return null;
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": item.name,
      "item": item.url ? `${SITE_URL}${item.url}` : undefined,
    })),
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};
